import { extractYouTubeId, getYouTubeThumbnail } from "@/lib/utils";
import type { PlaylistReaction, PlaylistTrack } from "@/lib/playback";
import { supabase } from "./supabase";

export interface ArchiveEntry {
  submission_id?: string; // Submission ID from database (for Supabase data)
  round_id: string;
  round_name: string;
  song_title: string;
  artist: string;
  submitter_id: string;
  submitter: string;
  score: number;
  comments: string; // Pipe-delimited voter comments for backward compatibility
  youtube_url: string;
  spotify_uri?: string;
  season: string;
  rank?: string;
  fun_facts?: string[];
}

let masterPlaylistCache: ArchiveEntry[] | null = null;

export function clearArchiveCache(): void {
  masterPlaylistCache = null;
}

export async function fetchMasterPlaylist(forceRefresh: boolean = false, userId?: string): Promise<ArchiveEntry[]> {
  console.log('[Archive] fetchMasterPlaylist called', { forceRefresh, hasCache: !!masterPlaylistCache, userId });
  
  if (masterPlaylistCache && !forceRefresh) {
    console.log('[Archive] ✅ Using cached data (', masterPlaylistCache.length, 'entries)');
    return masterPlaylistCache;
  }

  // Check if Supabase is configured
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const isSupabaseConfigured = !!(supabaseUrl && supabaseKey && 
    supabaseUrl !== 'https://placeholder.supabase.co' && 
    supabaseKey !== 'placeholder-key');

  console.log('[Archive] Configuration:', {
    supabaseConfigured: isSupabaseConfigured,
    supabaseUrl: supabaseUrl ? '✅ Set' : '❌ Not set',
    supabaseKey: supabaseKey ? '✅ Set' : '❌ Not set',
  });

  if (isSupabaseConfigured) {
    try {
      console.log('[Archive] 📊 Fetching from Supabase...');
      
      // Get user's league memberships if userId is provided
      let userLeagueIds: string[] = [];
      if (userId) {
        const { data: leagueMembers, error: membersError } = await supabase
          .from('league_members')
          .select('league_id')
          .eq('user_id', userId);
        
        if (membersError) {
          console.warn('[Archive] ⚠️ Could not fetch user leagues:', membersError);
        } else {
          userLeagueIds = leagueMembers?.map(m => m.league_id) || [];
          console.log(`[Archive] 📊 User is member of ${userLeagueIds.length} leagues`);
        }
      }
      
      // Fetch submissions filtered by user's leagues (more efficient than fetching all then filtering)
      let submissions: any[] = [];
      
      if (userId && userLeagueIds.length > 0) {
        // First, get all rounds from user's leagues
        const { data: rounds, error: roundsError } = await supabase
          .from('rounds')
          .select('id, league_id')
          .in('league_id', userLeagueIds);
        
        if (roundsError) {
          console.error('[Archive] ⚠️ Error fetching rounds for league filtering:', roundsError);
          // Don't throw - fall back to fetching all submissions
          console.log('[Archive] ⚠️ Falling back to fetching all submissions');
        } else {
          const roundIds = rounds?.map(r => r.id) || [];
          console.log(`[Archive] 📊 Found ${roundIds.length} rounds in user's ${userLeagueIds.length} leagues`);
          
          if (roundIds.length > 0) {
            // Fetch submissions from those rounds (with pagination)
            // Handle both: batching round IDs (Supabase .in() limit) and paginating submissions
            const roundIdBatchSize = 1000; // Supabase .in() limit
            const submissionPageSize = 1000;
            
            // Process rounds in batches
            for (let roundBatchStart = 0; roundBatchStart < roundIds.length; roundBatchStart += roundIdBatchSize) {
              const roundBatch = roundIds.slice(roundBatchStart, roundBatchStart + roundIdBatchSize);
              console.log(`[Archive] 🔄 Processing round batch ${Math.floor(roundBatchStart / roundIdBatchSize) + 1} (${roundBatch.length} rounds)`);
              
              // Paginate submissions for this batch of rounds
              let submissionFrom = 0;
              let hasMore = true;
              
              while (hasMore) {
                const { data: page, error: pageError } = await supabase
                  .from('submissions')
                  .select('id, round_id, user_id, song_title, artist_name, total_points, youtube_url, spotify_url, rank, fun_facts')
                  .in('round_id', roundBatch)
                  .order('created_at', { ascending: false })
                  .range(submissionFrom, submissionFrom + submissionPageSize - 1);
                
                if (pageError) {
                  console.error('[Archive] ❌ Error fetching submissions page:', pageError);
                  console.error('[Archive] ❌ Error details:', JSON.stringify(pageError, null, 2));
                  // Continue to next batch instead of breaking completely
                  hasMore = false;
                  continue;
                }
                
                if (!page || page.length === 0) {
                  // No more submissions for this batch of rounds
                  hasMore = false;
                  break;
                }
                
                submissions = submissions.concat(page);
                console.log(`[Archive] 📄 Fetched ${page.length} submissions (total: ${submissions.length}) from round batch ${Math.floor(roundBatchStart / roundIdBatchSize) + 1}, page ${Math.floor(submissionFrom / submissionPageSize) + 1}`);
                
                if (page.length < submissionPageSize) {
                  // Last page for this batch of rounds
                  hasMore = false;
                  break;
                }
                
                submissionFrom += submissionPageSize;
              }
            }
            
            console.log(`[Archive] ✅ Fetched ${submissions.length} submissions from user's leagues`);
          } else {
            console.log('[Archive] ⚠️ No rounds found in user\'s leagues');
          }
        }
        
        // If we didn't get any submissions from filtered query, fall back to fetching all and filtering
        if (submissions.length === 0) {
          console.log('[Archive] ⚠️ No submissions found from filtered query, fetching all submissions and filtering by league');
          
          // Fetch all submissions (with pagination)
          let from = 0;
          const pageSize = 1000;
          let allSubmissions: any[] = [];
          
          while (true) {
            const { data: page, error: pageError } = await supabase
              .from('submissions')
              .select('id, round_id, user_id, song_title, artist_name, total_points, youtube_url, spotify_url, rank, fun_facts')
              .order('created_at', { ascending: false })
              .range(from, from + pageSize - 1);
            
            if (pageError) {
              console.error('[Archive] ❌ Error fetching submissions page:', pageError);
              if (allSubmissions.length === 0) {
                throw new Error(`Failed to load archive from Supabase: ${pageError.message}`);
              }
              break;
            }
            
            if (!page || page.length === 0) break;
            
            allSubmissions = allSubmissions.concat(page);
            console.log(`[Archive] 📄 Fetched page: ${allSubmissions.length} total entries so far...`);
            
            if (page.length < pageSize) break; // Last page
            from += pageSize;
          }
          
          // Filter by league membership if user is provided
          if (userId && userLeagueIds.length > 0 && allSubmissions.length > 0) {
            // Get rounds to filter by league
            const roundIds = [...new Set(allSubmissions.map(s => s.round_id))];
            const { data: rounds } = await supabase
              .from('rounds')
              .select('id, league_id')
              .in('id', roundIds);
            
            if (rounds) {
              const roundLeagueMap = new Map(rounds.map(r => [r.id, r.league_id]));
              submissions = allSubmissions.filter(s => {
                const roundLeagueId = roundLeagueMap.get(s.round_id);
                return roundLeagueId && userLeagueIds.includes(roundLeagueId);
              });
              console.log(`[Archive] 🔍 Filtered ${allSubmissions.length} → ${submissions.length} submissions by league membership`);
            } else {
              submissions = allSubmissions;
            }
          } else {
            submissions = allSubmissions;
          }
        }
      } else {
        // No user or no league memberships - fetch all submissions (with pagination)
        let from = 0;
        const pageSize = 1000;
        
        while (true) {
          const { data: page, error: pageError } = await supabase
            .from('submissions')
            .select('id, round_id, user_id, song_title, artist_name, total_points, youtube_url, spotify_url, rank, fun_facts')
            .order('created_at', { ascending: false })
            .range(from, from + pageSize - 1);
          
          if (pageError) {
            console.error('[Archive] ❌ Error fetching submissions page:', pageError);
            if (submissions.length === 0) {
              throw new Error(`Failed to load archive from Supabase: ${pageError.message}`);
            }
            break;
          }
          
          if (!page || page.length === 0) break;
          
          submissions = submissions.concat(page);
          console.log(`[Archive] 📄 Fetched page: ${submissions.length} total entries so far...`);
          
          if (page.length < pageSize) break; // Last page
          from += pageSize;
        }
      }
      
      const submissionsError = null;

      if (submissionsError) {
        console.error('[Archive] ❌ Error fetching archived submissions:', submissionsError);
        throw new Error(`Failed to load archive from Supabase: ${submissionsError.message}`);
      }

      if (!submissions || submissions.length === 0) {
        console.warn('[Archive] ⚠️ No archived submissions found in Supabase, falling back to JSON');
        // Fall back to JSON if no archived data in Supabase
        return fetchFromJSON();
      }

      console.log('[Archive] ✅ Fetched', submissions.length, 'submissions from Supabase');
      
      // Count submissions with fun_facts
      const withFunFacts = submissions.filter(s => s.fun_facts && Array.isArray(s.fun_facts) && s.fun_facts.length > 0).length;
      console.log('[Archive] 📊 Fun facts:', withFunFacts, 'submissions have fun facts');

      // Fetch rounds for all submissions
      const roundIds = [...new Set(submissions.map(s => s.round_id))];
      console.log('[Archive] 📊 Fetching', roundIds.length, 'unique rounds...');
      const { data: rounds, error: roundsError } = await supabase
        .from('rounds')
        // NOTE: New schema stores season name on seasons.name (rounds only has season_id)
        .select('id, theme, round_number, season_id, league_id, seasons:seasons!rounds_season_id_fkey(name)')
        .in('id', roundIds);

      if (roundsError) {
        console.error('[Archive] ❌ Error fetching rounds:', roundsError);
      } else {
        console.log('[Archive] ✅ Fetched', rounds?.length || 0, 'rounds from Supabase');
      }

      // Fetch users (submitters) for all submissions
      const userIds = [...new Set(submissions.map(s => s.user_id).filter(Boolean))];
      console.log('[Archive] 📊 Fetching', userIds.length, 'unique users...');
      const users: any[] = [];
      let usersError: any = null;
      const userBatchSize = 1000;
      for (let i = 0; i < userIds.length; i += userBatchSize) {
        const batch = userIds.slice(i, i + userBatchSize);
        const { data: batchUsers, error: batchError } = await supabase
          .from('users')
          .select('id, username')
          .in('id', batch);
        if (batchError) {
          usersError = batchError;
          break;
        }
        users.push(...(batchUsers || []));
      }

      if (usersError) {
        console.error('[Archive] ❌ Error fetching users:', usersError);
      } else {
        console.log('[Archive] ✅ Fetched', users?.length || 0, 'users from Supabase');
      }

      // Fetch votes for all rounds to get voter comments
      // IMPORTANT: fetching by submission_id can exceed PostgREST/Supabase "IN clause" limits when there are many submissions.
      // Fetching by round_id is equivalent for our purposes and is far safer (usually a much smaller set).
      console.log('[Archive] 📊 Fetching votes/comments for', roundIds.length, 'rounds...');
      const { data: votes, error: votesError } = await supabase
        .from('votes')
        .select('submission_id, user_id, points, comment')
        .in('round_id', roundIds);

      if (votesError) {
        // Non-fatal: we can still build the archive without comments
        console.error('[Archive] ❌ Error fetching votes:', votesError);
      } else {
        const votesWithComments = votes?.filter(v => v.comment && v.comment.trim().length > 0).length || 0;
        console.log('[Archive] ✅ Fetched', votes?.length || 0, 'votes from Supabase (', votesWithComments, 'with comments)');
      }

      // Fetch voter usernames
      const voterUserIds = votes ? [...new Set(votes.map(v => v.user_id).filter(Boolean))] : [];
      const voterUsers: any[] = [];
      let voterUsersError: any = null;
      for (let i = 0; i < voterUserIds.length; i += userBatchSize) {
        const batch = voterUserIds.slice(i, i + userBatchSize);
        const { data: batchUsers, error: batchError } = await supabase
          .from('users')
          .select('id, username')
          .in('id', batch);
        if (batchError) {
          voterUsersError = batchError;
          break;
        }
        voterUsers.push(...(batchUsers || []));
      }

      if (voterUsersError) {
        console.error('Error fetching voter users:', voterUsersError);
      }

      // Create lookup maps
      const roundsMap = new Map(rounds?.map(r => [r.id, r]) || []);
      const usersMap = new Map(users?.map(u => [u.id, u]) || []);
      const voterUsersMap = new Map(voterUsers?.map(u => [u.id, u]) || []);

      // Group votes by submission_id
      const votesBySubmission = new Map<string, Array<{ username: string; points: number; comment: string }>>();
      if (votes) {
        votes.forEach(vote => {
          if (!votesBySubmission.has(vote.submission_id)) {
            votesBySubmission.set(vote.submission_id, []);
          }
          const submissionVotes = votesBySubmission.get(vote.submission_id)!;
          const voterUser = voterUsersMap.get(vote.user_id);
          if (vote.comment || vote.points !== 0) {
            submissionVotes.push({
              username: voterUser?.username || 'Unknown',
              points: vote.points,
              comment: vote.comment || '',
            });
          }
        });
      }

      // Transform Supabase data to ArchiveEntry format
      console.log('[Archive] 🔄 Transforming Supabase data to ArchiveEntry format...');
      const archiveEntries: ArchiveEntry[] = submissions.map((submission) => {
        const round = roundsMap.get(submission.round_id);
        const submitterUser = usersMap.get(submission.user_id);
        const submitterUsername = submitterUser?.username || 'Unknown';
        
        // Reconstruct comments string from votes (for backward compatibility)
        const submissionVotes = votesBySubmission.get(submission.id) || [];
        const commentsString = submissionVotes
          .map(v => `${v.username}: ${v.points}: ${v.comment}`)
          .join(' | ');

        return {
          submission_id: submission.id, // Include actual submission ID
          round_id: submission.round_id,
          round_name: round?.theme || `Round ${round?.round_number || ''}`,
          song_title: submission.song_title,
          artist: submission.artist_name,
          submitter_id: submission.user_id,
          submitter: submitterUsername,
          score: submission.total_points || 0,
          comments: commentsString, // Voter comments only (archive doesn't have submitter comments)
          youtube_url: submission.youtube_url || '',
          spotify_uri: submission.spotify_url ? `spotify:track:${submission.spotify_url.split('/').pop()}` : undefined,
          season: (round as any)?.seasons?.name || 'Unknown Season',
          rank: submission.rank !== null && submission.rank !== undefined ? String(submission.rank) : undefined,
          fun_facts: submission.fun_facts || undefined,
        };
      });

      // Log sample entry for debugging
      if (archiveEntries.length > 0) {
        const sample = archiveEntries[0];
        console.log('[Archive] 📋 Sample entry:', {
          song: `"${sample.song_title}" by ${sample.artist}`,
          round: sample.round_name,
          round_id: sample.round_id.substring(0, 12) + '...',
          season: sample.season,
          hasComments: !!sample.comments && sample.comments.length > 0,
          commentsLength: sample.comments?.length || 0,
          hasFunFacts: !!sample.fun_facts && Array.isArray(sample.fun_facts) && sample.fun_facts.length > 0,
          funFactsCount: sample.fun_facts?.length || 0,
        });
      }

      // Only cache if not forcing refresh
      if (!forceRefresh) {
        masterPlaylistCache = archiveEntries;
        console.log('[Archive] 💾 Cached', archiveEntries.length, 'entries');
      }
      console.log('[Archive] ✅ Returning', archiveEntries.length, 'entries from Supabase');
      return archiveEntries;
    } catch (error) {
      console.error('[Archive] ❌ Error fetching from Supabase, falling back to JSON:', error);
      return fetchFromJSON();
    }
  } else {
    // Fall back to JSON if Supabase is not configured
    console.log('[Archive] ⚠️ Supabase not configured, falling back to JSON');
    return fetchFromJSON();
  }
}

async function fetchFromJSON(): Promise<ArchiveEntry[]> {
  console.log('[Archive] 📄 Fetching from JSON file...');
  const response = await fetch("/data/playlists/master_songs.json");
  if (!response.ok) {
    console.error('[Archive] ❌ Failed to load JSON file');
    throw new Error("Failed to load playlist archive.");
  }

  const data = (await response.json()) as ArchiveEntry[];
  console.log('[Archive] ✅ Loaded', data.length, 'entries from JSON file');
  
  // Log sample entry
  if (data.length > 0) {
    const sample = data[0];
    console.log('[Archive] 📋 Sample JSON entry:', {
      song: `"${sample.song_title}" by ${sample.artist}`,
      round: sample.round_name,
      hasComments: !!sample.comments && sample.comments.length > 0,
      hasFunFacts: !!sample.fun_facts && Array.isArray(sample.fun_facts) && sample.fun_facts.length > 0,
    });
  }
  
  // Cache JSON data
  masterPlaylistCache = data;
  console.log('[Archive] 💾 Cached', data.length, 'entries from JSON');
  return data;
}

export function mapArchiveEntryToTrack(entry: ArchiveEntry): PlaylistTrack | null {
  const youtubeId = extractYouTubeId(entry.youtube_url);
  if (!youtubeId) {
    return null;
  }

  const reactions = parseArchiveComments(entry);

  // Use actual submission ID if available, otherwise fall back to concatenated string for backward compatibility
  const submissionId = entry.submission_id || `${entry.round_id}-${entry.submitter_id}`;

  return {
    submissionId,
    roundId: entry.round_id,
    leagueId: undefined,
    title: entry.song_title,
    artist: entry.artist,
    youtubeId,
    submitter: entry.submitter,
    comment: undefined, // Archive entries don't have submitter comments (only voter comments in reactions)
    thumbnail: getYouTubeThumbnail(youtubeId, "mqdefault"),
    roundNumber: undefined,
    reactions,
    fun_facts: entry.fun_facts,
  };
}

function parseArchiveComments(entry: ArchiveEntry): PlaylistReaction[] {
  if (!entry.comments) {
    return [];
  }

  const segments = entry.comments.split("|");
  const reactions: PlaylistReaction[] = [];

  segments.forEach((segment, index) => {
    const trimmed = segment.trim();
    if (!trimmed) {
      return;
    }

    const match = trimmed.match(/^\s*([^:]+):\s*([-+]?\d+(?:\.\d+)?)\s*:\s*(.*)$/);
    if (match) {
      const [, usernameRaw, pointsRaw, commentRaw] = match;
      reactions.push({
        id: `${entry.round_id}-${entry.submitter_id}-comment-${index}`,
        username: usernameRaw.trim(),
        points: Number(pointsRaw),
        comment: commentRaw.trim(),
      });
    } else {
      reactions.push({
        id: `${entry.round_id}-${entry.submitter_id}-comment-${index}`,
        username: "Judge",
        points: 0,
        comment: trimmed,
      });
    }
  });

  return reactions;
}

export interface SeasonGrouping {
  season: string;
  rounds: Record<string, ArchiveEntry[]>;
}

export function groupEntriesBySeasonAndRound(entries: ArchiveEntry[]): SeasonGrouping[] {
  const seasonMap = new Map<string, Record<string, ArchiveEntry[]>>();

  entries.forEach((entry) => {
    const seasonKey = entry.season || "Unknown Season";
    const rounds = seasonMap.get(seasonKey) ?? {};
    const bucket = rounds[entry.round_name] ?? [];
    bucket.push(entry);
    rounds[entry.round_name] = bucket;
    seasonMap.set(seasonKey, rounds);
  });

  return Array.from(seasonMap.entries())
    .map(([season, rounds]) => ({
      season,
      rounds,
    }))
    .sort((a, b) => a.season.localeCompare(b.season, undefined, { numeric: true, sensitivity: "base" }));
}


